import { API_ENDPOINTS } from '../endpoints';
import { getAuthHeaders, handleResponse } from '../client';
import type { CellNoteUpdateDTO, ColumnWidthSaveDTO, MilestoneRelationConfigDTO, PageResponse, ScheduleCreateDTO, ScheduleDetailVO, ScheduleMilestoneUpdateDTO, ScheduleVO, SpecPauseDTO, SpecScheduleCreateDTO, SpecScheduleVO, SpecTerminateDTO } from '../types';

export const scheduleApi = {
  /** includeDetail 仅项目排期详情页使用，列表项会附带 specs/columns/relations */
  getListByProject: async (
    projectId: number,
    params?: { creatorIds?: number[]; status?: string; pageNum?: number; pageSize?: number; includeDetail?: boolean }
  ): Promise<ScheduleVO[]> => {
    const query = new URLSearchParams();
    query.set('ProjectId', String(projectId));
    query.set('PageNum', String(params?.pageNum ?? 1));
    query.set('PageSize', String(params?.pageSize ?? 1000));
    if (params?.status) query.set('Status', params.status);
    if (params?.creatorIds?.length) query.set('CreatorIds', params.creatorIds.join(','));
    if (params?.includeDetail) query.set('IncludeDetail', 'true');
    const response = await fetch(`${API_ENDPOINTS.SCHEDULES.LIST}?${query.toString()}`, {
      headers: getAuthHeaders(),
    });
    const page = await handleResponse<PageResponse<ScheduleVO>>(response);
    return page.list ?? [];
  },

  getById: async (id: number): Promise<ScheduleDetailVO> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.DETAIL_FULL(id), {
      headers: getAuthHeaders(),
    });
    return handleResponse<ScheduleDetailVO>(response);
  },

  create: async (data: ScheduleCreateDTO): Promise<ScheduleVO> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ScheduleVO>(response);
  },

  update: async (id: number, data: {
    name?: string;
    expected_launch_date?: string;
    version_notes?: string;
    milestone_sort_order?: 'asc' | 'desc';
  }): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.UPDATE(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },

  delete: async (id: number): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.DELETE(id), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    // 后端成功时可能返回 res_content: ""，不应按 boolean 解析
    await handleResponse<unknown>(response);
    return true;
  },

  publish: async (id: number): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.PUBLISH(id), {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<boolean>(response);
  },

  refreshMilestoneStatus: async (): Promise<number> => {
    const response = await fetch(API_ENDPOINTS.SCHEDULES.REFRESH_MILESTONE_STATUS, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<number>(response);
  },
};


export const specApi = {
  create: async (scheduleId: number, data: SpecScheduleCreateDTO): Promise<SpecScheduleVO> => {
    const response = await fetch(API_ENDPOINTS.SPECS.CREATE(scheduleId), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SpecScheduleVO>(response);
  },

  update: async (scheduleId: number, specId: number, data: { spec_name: string; status?: string }): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.SPECS.UPDATE(scheduleId, specId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },

  replaceSkus: async (scheduleId: number, specId: number, ipdSkuIds: number[]): Promise<SpecScheduleVO> => {
    const response = await fetch(API_ENDPOINTS.SPECS.REPLACE_SKUS(scheduleId, specId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ipd_sku_ids: ipdSkuIds }),
    });
    return handleResponse<SpecScheduleVO>(response);
  },

  delete: async (scheduleId: number, specId: number): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.SPECS.DELETE(scheduleId, specId), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    // 后端成功时可能返回 res_content: ""，不应按 boolean 解析
    await handleResponse<unknown>(response);
    return true;
  },

  pause: async (scheduleId: number, specId: number, data: SpecPauseDTO): Promise<SpecScheduleVO> => {
    const response = await fetch(API_ENDPOINTS.SPECS.PAUSE(scheduleId, specId), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SpecScheduleVO>(response);
  },

  terminate: async (scheduleId: number, specId: number, data: SpecTerminateDTO): Promise<SpecScheduleVO> => {
    const response = await fetch(API_ENDPOINTS.SPECS.TERMINATE(scheduleId, specId), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SpecScheduleVO>(response);
  },
};


export const milestoneApi = {
  update: async (
    scheduleId: number,
    specId: number,
    milestoneId: number,
    data: ScheduleMilestoneUpdateDTO
  ): Promise<{ updated_milestones: any[] }> => {
    const response = await fetch(API_ENDPOINTS.MILESTONES.UPDATE(scheduleId, specId, milestoneId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ updated_milestones: any[] }>(response);
  },

  batchUpdate: async (
    scheduleId: number,
    data: { milestones: any[] }
  ): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.MILESTONES.BATCH(scheduleId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },
};


export const noteApi = {
  update: async (
    scheduleId: number,
    specId: number,
    milestoneId: number,
    data: CellNoteUpdateDTO
  ): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.NOTES.UPDATE(scheduleId, specId, milestoneId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },
};


export const relationApi = {
  get: async (scheduleId: number): Promise<any[]> => {
    const response = await fetch(API_ENDPOINTS.RELATIONS.GET(scheduleId), {
      headers: getAuthHeaders(),
    });
    return handleResponse<any[]>(response);
  },

  save: async (scheduleId: number, data: MilestoneRelationConfigDTO): Promise<{ updated_milestones: any[] }> => {
    const response = await fetch(API_ENDPOINTS.RELATIONS.SAVE(scheduleId), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ updated_milestones: any[] }>(response);
  },
};


export const columnWidthApi = {
  save: async (scheduleId: number, data: ColumnWidthSaveDTO): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.COLUMN_WIDTHS.SAVE(scheduleId), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },
};
